import type { LabManagedQuizResponses } from '@ludolio/shared-labs';
import { createLabsDemo, DEMO_PLAYTEST_QUIZ_ID as PLAYTEST_QUIZ_ID } from '@ludolio/shared-labs/demo';
import type { LabAdminTeam } from '@/services/api';
import { useDemoStore } from '@/stores/demoStore';
import type { ApiResponse, LabsSettings } from '@/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Handler = (body: any) => ApiResponse<unknown>;

const ok = <T,>(data: T): ApiResponse<T> => ({ success: true, data });
const DAY_MS = 86_400_000;
const GIB = 1024 * 1024 * 1024;
const daysAgo = (days: number) => new Date(Date.now() - days * DAY_MS).toISOString();
const nowIso = () => new Date().toISOString();

const playtestResponses = [
  { name: 'Jamie Jones', days: 3, fun: 'Yes', note: 'Trading felt slow at first' },
  { name: 'Indie Irwin', days: 5, fun: 'Yes', note: 'Loved the folding animation' },
  { name: 'Blake Brooks', days: 8, fun: 'Not yet', note: 'Winter arrived before I understood planting' },
];

let settings: LabsSettings = {
  organizationId: 'demo-org',
  slug: 'demo',
  labsUrl: 'https://labs.ludolio.com/demo',
  enabled: true,
  defaultVisibility: 'organization',
  remixingEnabled: false,
  internalRemixTerms: {
    version: '1',
    text: 'Members of this university may download this source, change it, and publish a derivative inside the same university. The derivative keeps attribution and the link to this version.\n\nThese terms do not allow sharing the source or its assets outside the university. Publishing beyond the university needs a separate licence.',
    acceptedAt: null,
  },
  storageQuotaBytes: 50 * GIB,
  storageUsedBytes: Math.round(12.4 * GIB),
};

let orphanedTeams: LabAdminTeam[] = [];

const studentEmail = (name: string) => `${name.toLowerCase().replace(' ', '.')}@demo-students.ludolio.com`;
const teamPerson = (name: string, role: LabAdminTeam['members'][number]['role']) => ({
  userId: `demo-${name.toLowerCase().replace(' ', '-')}`,
  name,
  email: studentEmail(name),
  role,
  inOrganization: true,
});

let ownedTeams: LabAdminTeam[] = [
  {
    id: 'demo-lab-team-kite',
    name: 'Kite Season',
    description: null,
    members: [teamPerson('Alex Adams', 'owner'), teamPerson('Blake Brooks', 'manager'), teamPerson('Chris Clark', 'member')],
    projects: [{ id: 'demo-lab-project-kite', title: 'Paper Kites' }],
    projectTotal: 1,
  },
  {
    id: 'demo-lab-team-tide',
    name: 'Tide Pool Club',
    description: null,
    members: [teamPerson('Alex Adams', 'owner')],
    projects: [],
    projectTotal: 0,
  },
];

function findDemoPerson(email: string) {
  const data = useDemoStore.getState().data;
  const people = data ? [...data.students, ...data.teachers, ...data.librarians] : [];
  return people.find((person) => person.email.toLowerCase() === email);
}

const csvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;

export const labsDemoHandlers: Record<string, Handler> = {
  ...createLabsDemo({
    actorName: 'Demo Librarian',
    findUnit: (unitId) => useDemoStore.getState().data?.units.find((unit) => unit.id === unitId),
  }),

  // ---------- org settings ----------
  '/labs/admin/settings/get': () => ok(settings),
  '/labs/admin/settings/update': ({ defaultVisibility, remixingEnabled, acceptInternalRemixTerms }) => {
    const accepted = settings.internalRemixTerms.acceptedAt ?? (acceptInternalRemixTerms ? nowIso() : null);
    if (remixingEnabled && !accepted) throw new Error('Accept the internal remix terms to allow remixing');
    settings = {
      ...settings,
      ...(defaultVisibility ? { defaultVisibility } : {}),
      ...(remixingEnabled !== undefined ? { remixingEnabled } : {}),
      internalRemixTerms: { ...settings.internalRemixTerms, acceptedAt: accepted },
    };
    return ok(settings);
  },

  // ---------- teams ----------
  '/labs/admin/teams/orphaned': ({ page = 1, pageSize = 20 }) =>
    ok({ teams: orphanedTeams.slice((page - 1) * pageSize, page * pageSize), page, pageSize, total: orphanedTeams.length }),
  '/labs/admin/teams/assign-owner': ({ teamId }) => {
    orphanedTeams = orphanedTeams.filter((team) => team.id !== teamId);
    return ok({ success: true });
  },
  '/labs/admin/teams/owned': ({ email }) => {
    const address = String(email ?? '').trim().toLowerCase();
    const isOwner = (team: LabAdminTeam) => team.members.some((member) => member.role === 'owner' && member.email === address);
    const teams = ownedTeams.filter(isOwner);
    const ownerUserId = teams[0]?.members.find((member) => member.email === address)?.userId ?? null;
    return ok({ ownerUserId, teams });
  },
  '/labs/admin/teams/move-owner': ({ teamId, fromUserId, email }) => {
    const address = String(email ?? '').trim().toLowerCase();
    const person = findDemoPerson(address);
    if (!person) throw new Error('No one in your university with this email has signed in to Ludolio yet');
    const team = ownedTeams.find((item) => item.id === teamId);
    const from = team?.members.find((member) => member.userId === fromUserId && member.role === 'owner');
    if (!team || !from) throw new Error('This person is no longer an owner of this team');
    if (from.email === address) throw new Error('Choose someone else');
    const name = [person.firstName, person.lastName].filter(Boolean).join(' ');
    const existing = team.members.find((member) => member.email === address);
    const members = team.members.map((member) => {
      if (member === from) return { ...member, role: 'manager' as const };
      if (member === existing) return { ...member, role: 'owner' as const };
      return member;
    });
    if (!existing) members.push({ userId: person.id, name, email: address, role: 'owner', inOrganization: true });
    ownedTeams = ownedTeams.map((item) => (item.id === teamId ? { ...item, members } : item));
    return ok({ success: true });
  },

  // ---------- quizzes ----------
  '/labs/quizzes/responses': ({ quizId }): ApiResponse<LabManagedQuizResponses> => {
    if (quizId !== PLAYTEST_QUIZ_ID) throw new Error('Quiz not found');
    const yes = playtestResponses.filter((item) => item.fun === 'Yes').length;
    return ok({
      canExport: true,
      summary: {
        responseCount: playtestResponses.length,
        questions: [
          { id: 'q1', prompt: 'Was the game fun?', options: [{ id: 'yes', label: 'Yes', count: yes }, { id: 'no', label: 'Not yet', count: playtestResponses.length - yes }] },
          { id: 'q2', prompt: 'What should we change?' },
        ],
      },
      responses: {
        items: playtestResponses.map((item, index) => ({
          id: `demo-lab-response-${index + 1}`,
          submittedAt: daysAgo(item.days),
          respondent: { name: item.name, email: `${item.name.toLowerCase().replace(' ', '.')}@demo-students.ludolio.com` },
          answers: [
            { questionId: 'q1', prompt: 'Was the game fun?', answer: item.fun },
            { questionId: 'q2', prompt: 'What should we change?', answer: item.note },
          ],
        })),
        page: 1,
        pageSize: 20,
        total: playtestResponses.length,
      },
    });
  },
  '/labs/quizzes/export': ({ format }) => {
    const rows = playtestResponses.map((item) => ({ respondent: item.name, fun: item.fun, change: item.note }));
    if (format === 'json') {
      return ok({ filename: 'playtest-feedback.json', contentType: 'application/json', body: JSON.stringify(rows, null, 2) });
    }
    const lines = ['Respondent,Was the game fun?,What should we change?', ...rows.map((row) => [row.respondent, row.fun, row.change].map(csvCell).join(','))];
    return ok({ filename: 'playtest-feedback.csv', contentType: 'text/csv', body: lines.join('\n') });
  },
};
